import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PolicyDocumentPreviewRequest } from 'app/models/policies-and-procedures/PolicyDocumentPreviewRequest';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { ProcedureFormView } from 'app/models/policies-and-procedures/ProcedureFormView';
import { DynamicFormUtilityServices } from '../dynamic-form-utility.service';
import {
	PolicyWorkspacePage,
	WORKSPACE_PAGE_SIZE,
} from 'app/client/policies-and-procedures/v2/policies-procedures-ai.constants';

@Injectable({
	providedIn: 'root',
})
export class ProcedureFormService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	private readonly urlPath = `${environment.apiUrl}policies-procedures/clients/`;

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private dynamicFormUtilityServices: DynamicFormUtilityServices
	) {}

	createForm(clientId: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/procedure-form`, this.setupRequest(request, prefix))
			.pipe();
	}

	private setupRequest(request: any, prefix: string) {
		const formData: FormData = new FormData();
		formData.append('document', request.document);
		formData.append('model', JSON.stringify(request));
		this.dynamicFormUtilityServices.appendDocuments(formData, prefix, request);

		return formData;
	}
	updateForm(clientId: string, id: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}`, this.setupRequest(request, prefix))
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/forms/procedure-form/${formId}/docs/${docId}`);
	}
	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}`).pipe();
	}

	getAllForms(clientId: string, request: any): Observable<any[]> {
		return this.httpClient.post<any[]>(`${this.urlPath}${clientId}/forms/procedure-form/all`, request).pipe();
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/forms/procedure-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/forms/procedure-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string, deleteParents = false) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/forms/procedure-form/${id}/${deleteParents}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<ProcedureFormView> {
		return this.httpClient
			.post<ProcedureFormView>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getProcedure(clientId: string, policyId: string) {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/forms/procedure-form/get-procedure/${policyId}`);
	}

	getWorkspaceDocuments(clientId: string, page = 1, pageSize = WORKSPACE_PAGE_SIZE, search = '', status = '') {
		const params = new HttpParams()
			.set('page', String(page))
			.set('pageSize', String(pageSize))
			.set('search', search || '')
			.set('status', status || '');
		return this.httpClient.get<PolicyWorkspacePage>(`${this.urlPath}${clientId}/forms/procedure-form/workspace`, {
			params,
		});
	}

	exportToPdf(clientId: string, formId: string): any {
		return this.httpClient
			.get(`${this.urlPath}${clientId}/forms/procedure-form/export-pdf/${formId}`, {
				responseType: 'blob',
			})
			.pipe();
	}

	/** Renders unsaved editor content with the document template; nothing is saved. */
	previewPdf(clientId: string, request: PolicyDocumentPreviewRequest): Observable<Blob> {
		return this.httpClient.post(`${this.urlPath}${clientId}/forms/procedure-form/preview-pdf`, request, {
			responseType: 'blob',
		});
	}

	generateAi(
		clientId: string,
		id: string,
		request: {
			prompt?: string;
			pastedText?: string;
			reuseExisting?: boolean;
			file?: File;
			/** Ask AI to change the current draft instead of generating from a source. */
			refineInstruction?: string;
			/** The draft on screen when refining, so unsaved edits are kept. */
			currentBodyMarkdown?: string;
		}
	) {
		const formData = new FormData();
		if (request.prompt) {
			formData.append('prompt', request.prompt);
		}
		if (request.pastedText) {
			formData.append('pastedText', request.pastedText);
		}
		formData.append('reuseExisting', String(!!request.reuseExisting));
		if (request.refineInstruction) {
			formData.append('refineInstruction', request.refineInstruction);
		}
		if (request.currentBodyMarkdown) {
			formData.append('currentBodyMarkdown', request.currentBodyMarkdown);
		}
		if (request.file) {
			formData.append('file', request.file);
		}
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/ai/generate`, formData);
	}

	generateChangeSummary(clientId: string, id: string, request: { reuseExisting?: boolean }) {
		return this.httpClient.post<any>(
			`${this.urlPath}${clientId}/forms/procedure-form/${id}/ai/change-summary`,
			request
		);
	}

	prepareV2Edit(clientId: string, id: string) {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/procedure-form/${id}/ai/prepare-edit`, {});
	}
}
